import React, { FC } from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { RestorationType, getRestorationCategory } from 'models/order';

type RestorationTypeAutocompleteProps = {
    value?: RestorationType | null;
    onChange: (value: RestorationType) => void;
    error?: boolean;
    helperText?: React.ReactNode;
};

const RestorationTypeAutocomplete: FC<RestorationTypeAutocompleteProps> = ({ value, onChange, error, helperText }) => {
    const { t: translate } = useTranslation(['manage-actions', 'restoration-types']);

    const options = Object.values(RestorationType).map((type) => ({
        value: type,
        label: translate(`restoration-types:${type}`),
        description: translate(`restoration-types:${type}_DESC`),
        category: getRestorationCategory(type),
        categoryLabel: translate(`restoration-types:${getRestorationCategory(type)}`),
    }));

    const selectedOption = value ? options.find((option) => option.value === value) ?? null : null;

    type Option = (typeof options)[number];

    const filterOptions = (allOptions: Option[], { inputValue }: { inputValue: string }): Option[] => {
        const query = inputValue.toLowerCase().trim();
        if (!query) return allOptions;

        const labelMatches = allOptions.filter((option) => option.label.toLowerCase().includes(query));
        const descriptionOnlyMatches = allOptions.filter(
            (option) => !option.label.toLowerCase().includes(query) && option.description.toLowerCase().includes(query),
        );

        return [...labelMatches, ...descriptionOnlyMatches];
    };

    return (
        <Autocomplete
            fullWidth
            sx={{ minWidth: 300 }}
            id="denthub-restoration-type-label"
            options={options}
            value={selectedOption}
            groupBy={(option) => option.categoryLabel}
            getOptionLabel={(option) => option.label}
            filterOptions={filterOptions}
            isOptionEqualToValue={(option, selected) => option.value === selected.value}
            onChange={(_e, option) => option && onChange(option.value as RestorationType)}
            renderOption={(props, option) => (
                <li {...props}>
                    <Box>
                        <Typography variant="body2">{option.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {option.description}
                        </Typography>
                    </Box>
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    required
                    label={translate('manage-actions:DENTHUB_RESTORATION_TYPE')}
                    error={error}
                    helperText={helperText}
                />
            )}
        />
    );
};

export default RestorationTypeAutocomplete;
